import getConfig from "next/config"
import Head from "next/head"
import Router from "next/router"
import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"

import { Header } from "~/app/Free/Header"
import { Note } from "~/app/Renew/Note"
import { RankingItem } from "~/app/Renew/Ranking/Ranking"
import { AdBanner, AdRectangle } from "~/components/Ad"
import { AdInfeed } from "~/components/Ad"
import { Microroad } from "~/components/Ad/Microroad"
import { Footer } from "~/components/Footer"
import { PageTop } from "~/components/PageTop"
import { adIds, distribution, microroadAdId } from "~/config"
import { ExperimentProvider } from "~/contexts/Experiment"
import { TrackingProvider } from "~/contexts/Tracking"
import { Fetcher } from "~/external/Fetcher"
import { useRepro } from "~/hooks/useRepro"
import { mixin } from "~/libs/style"
import { ConfigPresenter } from "~/presenter/ConfigPresenter"
import { RankingPresenter } from "~/presenter/RankingPresenter"
import { ConfigRepository } from "~/repository/ConfigRepository"
import { RankingRepository } from "~/repository/RankingRepository"

type RankingPageProps = Pick<ReturnType<typeof getStaticProps> extends Promise<infer R> ? R : never, "props">

const RankingPage: React.FC<RankingPageProps["props"]> = (props) => {
  const [rankingOffset, setRankingOffset] = useState(500)
  const rankingRef = useRef<HTMLDivElement>(null)
  const { distribution, adIds, ranking, config, configDomain, microroadAdId } = props
  const adConfig = adIds[distribution].ranking
  const adRect = adConfig.rectangle
  const adInfeedId = adConfig.infeed
  const adBanner = adIds[distribution].banner
  const baseUrl = configDomain.free
  const infeedAdPosition = 5
  useRepro(distribution)
  useEffect(() => {
    if (distribution !== "portal" && distribution !== "free") {
      Router.push("/")
    }

    const offsetAfterMount = rankingRef?.current?.offsetTop
    offsetAfterMount && setRankingOffset(offsetAfterMount)
  }, [rankingOffset])

  return (
    <ExperimentProvider configExperiments={config.experiments}>
      <Head>
        <link rel="canonical" href={`${baseUrl}/ranking`} />
        <meta name="description" content="イージーゲーム（簡単ゆる）人気ゲームランキング" />
        <meta name="keywords" content="無料ゲーム,無料,ゲーム,スマホゲーム,簡単ゲーム,カジュアルゲーム" />
        <meta name="apple-mobile-web-app-title" content="イージーゲーム（簡単ゆる）ランキング" />
        <title>人気ゲームランキング - イージーゲーム（簡単ゆる）|au Webポータル</title>
      </Head>
      <TrackingProvider categoryProp="人気ゲームランキング">
        <Container>
          <Header hasBackButton={true} backUrl={configDomain.free} />
          <AdBanner id={adBanner.id} url={adBanner.url} size={adBanner.size} />
          <RankingWrapper ref={rankingRef}>
            <Title>無料ゲームランキング一覧</Title>
            <Items>
              {ranking.map((gameCatalog, index) => {
                const items: JSX.Element[] = []
                const rank = index + 1
                if ((index || 1) % infeedAdPosition === 0) {
                  const targetId = `adg_infeed_ranking_${index}`
                  items.push(
                    <li key={targetId} className="js-legacy-infeed" suppressHydrationWarning>
                      <AdInfeed id={adInfeedId} targetId={targetId} />
                    </li>
                  )
                }
                items.push(<RankingItem key={index} gameCatalog={gameCatalog} rank={rank} isRankingPage={true} />)
                return items
              })}
            </Items>
          </RankingWrapper>
          <Note />
          <AdRectangle id={adRect.id} url={adRect.url} size={adRect.size} />
          <PageTop displayOffset={rankingOffset} />
          <Footer />
          <Microroad id={microroadAdId} />
        </Container>
      </TrackingProvider>
    </ExperimentProvider>
  )
}

const Container = styled.div`
  background: #f1f1f1;
  padding-bottom: 50px;
  @media (orientation: portrait) {
    padding-bottom: calc(50 * (100vw / 320));
  }
`

const Title = styled.h1`
  color: #3c3c3c;
  font-size: 16px;
  font-weight: 700;
`

const RankingWrapper = styled.section`
  background: #ffffff;
  margin: 8px auto 0;
  ${mixin.maxWidth}
  padding: 24px 12px;
  box-sizing: border-box;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
`

const Items = styled.ul`
  ${mixin.maxWidth}
  padding: 16px 0 24px;
  margin: 0 auto;
  background-color: white;
`

export const getStaticProps = async () => {
  const {
    serverRuntimeConfig: { cmsEndpoint, cmsApiKey, configDomain }
  } = getConfig()

  const fetcher = new Fetcher({ cmsEndpoint, cmsApiKey })

  const rankingRepo = new RankingRepository(fetcher)
  const ranking = await rankingRepo.findAll()

  const configRepo = new ConfigRepository(fetcher)
  const config = await configRepo.findAll()

  return {
    props: {
      ranking: RankingPresenter.transform(ranking),
      distribution,
      adIds,
      config: ConfigPresenter.transform(config),
      configDomain: configDomain,
      microroadAdId
    }
  }
}

export default RankingPage
